import React, { useState } from 'react';
import { View, Text, TextInput, Pressable, Switch, ScrollView, StyleSheet, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { DataFieldDefinition } from '../../shared/types/due-diligence';
import { Ionicons } from '@expo/vector-icons';

export interface DataFieldsFormProps {
  fields: DataFieldDefinition[];
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  disabled?: boolean;
}

export function DataFieldsForm({
  fields,
  values,
  onChange,
  disabled = false,
}: DataFieldsFormProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile } = useResponsive();
  const formStyles = makeFormStyles(tokens, utils, isMobile, isSmallMobile);

  if (!fields || fields.length === 0) {
    return null;
  }

  return (
    <View style={formStyles.container}>
      <View style={formStyles.header}>
        <Ionicons name="clipboard-outline" size={16} color={tokens.color.primary} />
        <Text style={formStyles.headerText}>Data Fields</Text>
      </View>
      <View style={formStyles.fieldsGrid}>
        {fields.map((field) => (
          <DataField
            key={field.key}
            field={field}
            value={values[field.key]}
            onChange={(val) => onChange(field.key, val)}
            disabled={disabled}
            tokens={tokens}
            utils={utils}
            isMobile={isMobile}
            isSmallMobile={isSmallMobile}
          />
        ))}
      </View>
    </View>
  );
}

interface DataFieldProps {
  field: DataFieldDefinition;
  value: any;
  onChange: (value: any) => void;
  disabled: boolean;
  tokens: ReturnType<typeof useStyles>['tokens'];
  utils: ReturnType<typeof useStyles>['utils'];
  isMobile: boolean;
  isSmallMobile: boolean;
}

function DataField({
  field,
  value,
  onChange,
  disabled,
  tokens,
  utils,
  isMobile,
  isSmallMobile,
}: DataFieldProps) {
  const fieldStyles = makeFieldStyles(tokens, utils, isMobile, isSmallMobile);

  // Render based on field type
  switch (field.type) {
    case 'string':
      return (
        <View style={fieldStyles.fieldContainer}>
          <Text style={fieldStyles.label}>
            {field.label}
            {field.required && <Text style={fieldStyles.required}> *</Text>}
          </Text>
          <TextInput
            style={fieldStyles.textInput}
            value={value || ''}
            onChangeText={onChange}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.4)}
            editable={!disabled}
          />
        </View>
      );

    case 'number':
      return (
        <View style={fieldStyles.fieldContainer}>
          <Text style={fieldStyles.label}>
            {field.label}
            {field.unit && <Text style={fieldStyles.unit}> ({field.unit})</Text>}
            {field.required && <Text style={fieldStyles.required}> *</Text>}
          </Text>
          <View style={fieldStyles.numberInputContainer}>
            <TextInput
              style={[fieldStyles.textInput, fieldStyles.numberInput]}
              value={value !== undefined && value !== null ? String(value) : ''}
              onChangeText={(text) => {
                const num = parseFloat(text);
                onChange(isNaN(num) ? null : num);
              }}
              placeholder={field.placeholder || '0'}
              placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.4)}
              keyboardType="decimal-pad"
              editable={!disabled}
            />
            {field.unit && (
              <Text style={fieldStyles.unitLabel}>{field.unit}</Text>
            )}
          </View>
        </View>
      );

    case 'boolean':
      return (
        <View style={fieldStyles.fieldContainer}>
          <View style={fieldStyles.booleanRow}>
            <Text style={fieldStyles.label}>
              {field.label}
              {field.required && <Text style={fieldStyles.required}> *</Text>}
            </Text>
            <Switch
              value={!!value}
              onValueChange={onChange}
              disabled={disabled}
              trackColor={{ 
                false: addColorOpacity(tokens.color.onSurface, 0.2),
                true: addColorOpacity(tokens.color.primary, 0.5)
              }}
              thumbColor={value ? tokens.color.primary : tokens.color.surface}
            />
          </View>
        </View>
      );

    case 'date':
      return (
        <View style={fieldStyles.fieldContainer}>
          <Text style={fieldStyles.label}>
            {field.label}
            {field.required && <Text style={fieldStyles.required}> *</Text>}
          </Text>
          <TextInput
            style={fieldStyles.textInput}
            value={value || ''}
            onChangeText={onChange}
            placeholder="YYYY-MM-DD"
            placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.4)}
            editable={!disabled}
          />
        </View>
      );

    case 'select':
      return (
        <View style={fieldStyles.fieldContainer}>
          <Text style={fieldStyles.label}>
            {field.label}
            {field.required && <Text style={fieldStyles.required}> *</Text>}
          </Text>
          <View style={fieldStyles.selectOptions}>
            {field.options?.map((option) => {
              const isSelected = value === option.value;
              return (
                <Pressable
                  key={option.value}
                  onPress={() => !disabled && onChange(option.value)}
                  style={({ pressed }) => [
                    fieldStyles.selectOption,
                    isSelected && fieldStyles.selectOptionSelected,
                    (pressed || disabled) && fieldStyles.selectOptionPressed,
                  ]}
                >
                  <Text style={[
                    fieldStyles.selectOptionText,
                    isSelected && fieldStyles.selectOptionTextSelected,
                  ]}>
                    {option.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      );

    case 'multiselect':
      const selectedValues = Array.isArray(value) ? value : [];
      return (
        <View style={fieldStyles.fieldContainer}>
          <Text style={fieldStyles.label}>
            {field.label}
            {field.required && <Text style={fieldStyles.required}> *</Text>}
          </Text>
          <View style={fieldStyles.selectOptions}>
            {field.options?.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <Pressable
                  key={option.value}
                  onPress={() => {
                    if (disabled) return;
                    if (isSelected) {
                      onChange(selectedValues.filter(v => v !== option.value));
                    } else {
                      onChange([...selectedValues, option.value]);
                    }
                  }}
                  style={({ pressed }) => [
                    fieldStyles.selectOption,
                    isSelected && fieldStyles.selectOptionSelected,
                    (pressed || disabled) && fieldStyles.selectOptionPressed,
                  ]}
                >
                  {isSelected && (
                    <Ionicons name="checkmark" size={14} color={tokens.color.primary} />
                  )}
                  <Text style={[
                    fieldStyles.selectOptionText,
                    isSelected && fieldStyles.selectOptionTextSelected,
                  ]}>
                    {option.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      );

    case 'array':
      return <ArrayField field={field} value={value} onChange={onChange} disabled={disabled} tokens={tokens} utils={utils} isMobile={isMobile} isSmallMobile={isSmallMobile} />;

    default:
      return (
        <View style={fieldStyles.fieldContainer}>
          <Text style={fieldStyles.label}>{field.label}</Text>
          <TextInput
            style={fieldStyles.textInput}
            value={value || ''}
            onChangeText={onChange}
            placeholder={`Enter ${field.label.toLowerCase()}...`}
            placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.4)}
            editable={!disabled}
          />
        </View>
      );
  }
}

// Array field with add/remove functionality
function ArrayField({
  field,
  value,
  onChange,
  disabled,
  tokens,
  utils,
  isMobile,
  isSmallMobile,
}: DataFieldProps) {
  const [newItem, setNewItem] = useState('');
  const fieldStyles = makeFieldStyles(tokens, utils, isMobile, isSmallMobile);
  const items = Array.isArray(value) ? value : [];

  const addItem = () => {
    if (newItem.trim()) {
      onChange([...items, newItem.trim()]);
      setNewItem('');
    }
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <View style={fieldStyles.fieldContainer}>
      <Text style={fieldStyles.label}>
        {field.label}
        {field.required && <Text style={fieldStyles.required}> *</Text>}
      </Text>
      
      {/* Existing items */}
      {items.length > 0 && (
        <View style={fieldStyles.arrayItems}>
          {items.map((item, index) => (
            <View key={index} style={fieldStyles.arrayItem}>
              <Text style={fieldStyles.arrayItemText} numberOfLines={1}>
                {item}
              </Text>
              {!disabled && (
                <Pressable
                  onPress={() => removeItem(index)}
                  style={fieldStyles.arrayItemRemove}
                >
                  <Ionicons name="close" size={14} color={tokens.color.error} />
                </Pressable>
              )}
            </View>
          ))}
        </View>
      )}
      
      {/* Add new item */}
      {!disabled && (
        <View style={fieldStyles.arrayAddRow}>
          <TextInput
            style={[fieldStyles.textInput, { flex: 1 }]}
            value={newItem}
            onChangeText={setNewItem}
            placeholder={`Add ${field.label.toLowerCase()}...`}
            placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.4)}
            onSubmitEditing={addItem}
          />
          <Pressable
            onPress={addItem}
            style={({ pressed }) => [
              fieldStyles.arrayAddButton,
              pressed && fieldStyles.arrayAddButtonPressed,
            ]}
          >
            <Ionicons name="add" size={20} color={tokens.color.primary} />
          </Pressable>
        </View>
      )}
    </View>
  );
}

function makeFormStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  isSmallMobile: boolean
) {
  return StyleSheet.create({
    container: {
      marginTop: utils.spacing.md,
      paddingTop: utils.spacing.md,
      borderTopWidth: 1,
      borderTopColor: tokens.color.border,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
      marginBottom: utils.spacing.md,
    },
    headerText: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.primary,
      fontFamily: utils.typography.body.fontFamily,
    },
    fieldsGrid: {
      gap: utils.spacing.md,
    },
  });
}

function makeFieldStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  isSmallMobile: boolean
) {
  return StyleSheet.create({
    fieldContainer: {
      gap: utils.spacing.xs,
    },
    label: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    required: {
      color: tokens.color.error,
    },
    unit: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.normal,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
    },
    textInput: {
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.md,
      paddingHorizontal: utils.spacing.md,
      paddingVertical: utils.spacing.sm,
      fontSize: utils.typography.sizes.base,
      color: tokens.color.onSurface,
      backgroundColor: tokens.color.surface,
      fontFamily: utils.typography.body.fontFamily,
      ...Platform.select({
        web: {
          outline: 'none',
        } as any,
      }),
    },
    numberInputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
    },
    numberInput: {
      flex: 1,
      maxWidth: 150,
    },
    unitLabel: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    booleanRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: utils.spacing.xs,
    },
    selectOptions: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.xs,
    },
    selectOption: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      borderRadius: utils.radius.sm,
      borderWidth: 1,
      borderColor: tokens.color.border,
      backgroundColor: tokens.color.surface,
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    selectOptionSelected: {
      borderColor: tokens.color.primary,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
    },
    selectOptionPressed: {
      opacity: 0.7,
    },
    selectOptionText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    selectOptionTextSelected: {
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.medium,
    },
    arrayItems: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.xs,
    },
    arrayItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      borderRadius: utils.radius.sm,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
      borderWidth: 1,
      borderColor: addColorOpacity(tokens.color.primary, 0.2),
    },
    arrayItemText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      maxWidth: 200,
    },
    arrayItemRemove: {
      padding: 2,
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    arrayAddRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
    },
    arrayAddButton: {
      width: 36,
      height: 36,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    arrayAddButtonPressed: {
      opacity: 0.7,
    },
  });
}

